import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { countries } from './countries';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const UserProfile = () => {
  const [userInfo, setUserInfo] = useState(null);
  const [showAvatarModal, setShowAvatarModal] = useState(false);
  const [showEditProfileModal, setShowEditProfileModal] = useState(false);
  const [selectedAvatar, setSelectedAvatar] = useState("profile.png");
  const [firstName, setFirstName] = useState("");
  const [inputFirstName, setInputFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [inputLastName, setInputLastName] = useState("");
  const [country, setCountry] = useState('');
  const [inputCountry, setInputCountry] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [chartData, setChartData] = useState(null);

  const apiUrl = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();

  const avatars = [
    "/avatars/avataaars_0.png",
    "/avatars/avataaars_1.png",
    "/avatars/avataaars_2.png",
    "/avatars/avataaars_3.png",
    "/avatars/avataaars_4.png",
    "/avatars/avataaars_5.png",
    "/avatars/avataaars_6.png",
    "/avatars/avataaars_7.png",
  ];

  useEffect(() => {
    // Set an SEO-friendly page title
    document.title = `Profile | Coding Canvas`;

    // Set meta description for SEO
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute("content", `Explore interactive tutorials, code samples, and exercises to learn programming visually on Coding Canvas.`);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('userToken');
    if (!token) {
      // If user is not logged in, redirect to home
      navigate('/');
      return;
    }

    // Fetch user info if logged in
    const fetchUserInfo = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${apiUrl}/api/user-info/`, {
          headers: { Authorization: `Token ${token}` },
        });
        const data = response.data;
        setUserInfo(data);
        setFirstName(data.first_name);
        setLastName(data.last_name);
        setCountry(data.country_code);
        setSelectedAvatar(data.avatar);
        fetchCompletedTopicsAndActivity();
      } catch (error) {
        if (error.response?.status === 401) {
          localStorage.removeItem('isLoggedIn');
          localStorage.removeItem('userToken');

          // Dispatch custom event to update login status across the app
          window.dispatchEvent(new Event('loginStatusChange'));

          navigate('/');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchUserInfo();
  }, [apiUrl, navigate]);


  const fetchCompletedTopicsAndActivity = async () => {
    const token = localStorage.getItem('userToken');
    if (!token) {
      navigate('/');
      return;
    }

    
    try {
      const response = await axios.get(`${apiUrl}/api/completed-topics-by-language/`, {
        headers: { Authorization: `Token ${token}` },
      });

      const { total_completed, recently_completed_topics, languages } = response.data;

      setUserInfo((prevInfo) => ({
        ...prevInfo,
        completedTopics: total_completed,
        recentActivity: recently_completed_topics,
        languages,
      }));

      // Prepare data for the chart
      const records = [];
      Object.entries(languages).forEach(([lang, langData]) => {
        Object.entries(langData.topics).forEach(([topic, topicData]) => {
          topicData.subtopics.forEach((subtopic) => {
            records.push({
              language: lang,
              subtopic: subtopic.display_name,
              completionDate: new Date(subtopic.completion_datetime),
            });
          });
        });
      });

      const sortedRecords = records.sort((a, b) => a.completionDate - b.completionDate);

      const labels = sortedRecords.map((record) =>
        record.completionDate.toLocaleString()
      );
      const datasets = Object.keys(languages).map((lang) => ({
        label: lang,
        data: sortedRecords
          .filter((record) => record.language === lang)
          .map((record) => sortedRecords.indexOf(record)),
        borderColor: getRandomColor(),
        fill: false,
      }));

      setChartData({ labels, datasets });
    } catch (error) {
      console.error('Error fetching completed topics and activity:', error);
    }
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getRandomColor = () =>
    `rgba(${Math.floor(Math.random() * 255)}, ${Math.floor(
      Math.random() * 255
    )}, ${Math.floor(Math.random() * 255)}, 1)`;


  const handleAvatarClick = () => setShowAvatarModal(true);
  const handleEditProfileClick = () => {
    setInputFirstName(firstName);
    setInputLastName(lastName);
    setInputCountry(country);
    setShowEditProfileModal(true);
  }

  const handleAvatarSelect = async (avatar) => {
    setSelectedAvatar(avatar.split('/').pop());
    const data = { avatar: avatar.split('/').pop() };
    await updateProfile(data);
  };

  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const updateProfile = async (data) => {
    const token = localStorage.getItem('userToken');
    const header = {
      headers: {
        Authorization: `Token ${token}`,
        'Content-Type': 'application/json'
      }
    };

    setMessage('');
    setLoading(true);
    await delay(1000);

    try {
      const response = await axios.patch(`${apiUrl}/api/update-profile/`, data, header);
      if (response.status === 200) {
        setMessage('Profile updated successfully!');
        setUserInfo((prevInfo) => ({
          ...prevInfo,
          ...data,
          first_name: firstName,
          last_name: lastName,
          country_code: country,
          avatar: selectedAvatar,
        }));
      }
    } catch (error) {
      setMessage('Sorry! try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    setFirstName(inputFirstName);
    setLastName(inputLastName);
    setCountry(inputCountry);

    const data = {
      country_code: inputCountry,
      first_name: inputFirstName,
      last_name: inputLastName,
    };
    await updateProfile(data);
  };

  // Spinner for loading user info on initial load
  if (userInfo === null) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="spinner-border" role="status">
          <span className="sr-only">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container my-5">
      {/* Profile Header */}
      <div className="card mb-4 shadow text-center">
        <div className="card-body">
          <div className="profile-avatar-wrapper" onClick={handleAvatarClick}>
            <div className="avatar-overlay"></div>
            <img
              src={`/avatars/${selectedAvatar}`}
              alt="User Profile"
              className="rounded-circle mb-3 profile-avatar"
            />
            <i className="fas fa-pencil-alt edit-icon"></i>
          </div>
          <h2 className="mb-0">{firstName} {lastName}</h2>
          <p className="text-muted">From: {country || "IND"}</p>
          {userInfo && userInfo.subscription ?
            <span className="badge bg-dark me-2">Premium Member</span>
            : <span className="badge bg-dark me-2">Free Member</span>
          }
          <button
            className="btn btn-outline-dark btn-sm custom-edit-btn"
            onClick={handleEditProfileClick}
          >
            <i className="fas fa-pencil-alt"></i> Edit Profile
          </button>
        </div>
      </div>

      {/* Avatar Selection Modal */}
      {showAvatarModal && (
        <div className="modal show d-block" tabIndex="-1" role="dialog">
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content modal-glass-avatar">
              <div className="modal-header">
                <h5 className="modal-title-avatar">Select Your Avatar</h5>
              </div>
              <div className="modal-body">
                <div className="d-flex flex-wrap justify-content-center">
                  {avatars.map((avatar, index) => (
                    <img
                      key={index}
                      src={avatar}
                      alt={`Avatar ${index + 1}`}
                      className="m-2 rounded-circle avatar-in-modal"
                      style={{ width: '80px', height: '80px', cursor: 'pointer' }}
                      onClick={() => handleAvatarSelect(avatar)}
                    />
                  ))}
                </div>
              </div>
              <div className="modal-footer">
                {/* Existing modal spinner behavior remains unchanged */}
                {loading && <div className="text-center my-2"><div className="spinner-border" role="status"></div></div>}
                {message && <p className="text-center mt-2">{message}</p>}
                <button type="button" className="btn btn-dark" onClick={() => {
                  setMessage('');
                  setShowAvatarModal(false);
                }}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Profile Modal */}
      {showEditProfileModal && (
        <div className="modal show d-block" tabIndex="-1" role="dialog">
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content modal-glass-avatar">
              <div className="modal-header">
                <h5 className="modal-title-avatar">Edit Profile</h5>
              </div>
              <div className="modal-body">
                <form>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label htmlFor="firstName" className="form-label">First Name</label>
                        <input
                          type="text"
                          className="form-control login-custom-input"
                          id="firstName"
                          value={inputFirstName}
                          onChange={(e) => setInputFirstName(e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label htmlFor="lastName" className="form-label">Last Name</label>
                        <input
                          type="text"
                          className="form-control login-custom-input"
                          id="lastName"
                          value={inputLastName}
                          onChange={(e) => setInputLastName(e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label htmlFor="registerCountry" className="form-label">Country</label>
                        <select
                          id="registerCountry"
                          className="form-control login-custom-input custom-dropdown-profile"
                          value={inputCountry}
                          onChange={(e) => setInputCountry(e.target.value)}
                        >
                          <option value="">Select your country</option>
                          {countries.map((country) => (
                            <option key={country.code} value={country.code}>
                              {country.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label htmlFor="email" className="form-label">Email</label>
                        <input
                          type="text"
                          className="form-control custom-input disabled-input-box"
                          id="email"
                          value={userInfo?.email}
                          disabled={true}
                        />
                      </div>
                    </div>
                  </div>
                </form>
              </div>
              <div className="modal-footer">
                {/* Existing modal spinner behavior remains unchanged */}
                {loading && <div className="text-center my-2"><div className="spinner-border" role="status"></div></div>}
                {message && <p className="text-center mt-2">{message}</p>}
                <button type="button" className="btn btn-dark" onClick={() => {
                  setMessage('');
                  setShowEditProfileModal(false);
                }}>Close</button>
                <button type="button" className="btn btn-success" onClick={handleSaveProfile}>Save Changes</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Profile Details Sections */}
      <div className="row">
        {/* Account Overview */}
        <div className="col-md-4 mb-4">
          <div className="card h-100 shadow">
            <div className="card-header bg-dark text-white text-center">Account Overview</div>
            <div className="card-body">
              <p><strong>Email:</strong> {userInfo?.email}</p>
              <p><strong>Member Since:</strong> {userInfo?.date_joined || "Unknown"}</p>
              <p className='badge bg-success'><strong>Subscription Plan:</strong> {userInfo?.subscription?.plan || "Free"}</p>
              {userInfo && userInfo.subscription ?
                <>
                  <p className='badge bg-success'><strong>Amount Paid:</strong> {userInfo?.subscription?.amount} {userInfo?.subscription?.currency}</p>
                  <p className='badge bg-success'><strong>Plan Expires At:</strong> {formatDate(userInfo?.subscription?.expires_at)}</p>
                </>
                : null
              }
            </div>
          </div>
        </div>

        {/* Learning Progress */}
        <div className="col-md-4 mb-4">
          <div className="card h-100 shadow">
            <div className="card-header bg-dark text-white text-center">Learning Progress</div>
            <div className="card-body">
              <h5>Completed Topics</h5>
              <p><span className="badge bg-success">{userInfo?.completedTopics || 0}</span> Topics</p>
            </div>
          </div>
        </div>

        {/* Certificates */}
        <div className="col-md-4 mb-4">
          <div className="card h-100 shadow">
            <div className="card-header bg-dark text-white text-center">Certificates</div>
            <div className="card-body">
              <ul className="list-unstyled">
                {userInfo?.certificates?.map((cert, index) => (
                  <li key={index}><i className="bi bi-award-fill text-success"></i> {cert}</li>
                )) || <li>No certificates earned yet.</li>}
              </ul>
              <button className="btn btn-outline-dark btn-sm">View All Certificates</button>
            </div>
          </div>
        </div>
      </div>

      {/* Additional Profile Sections */}
      <div className="row">
        {/* Favorite Topics */}
        <div className="col-md-6 mb-4">
          <div className="card h-100 shadow">
            <div className="card-header bg-dark text-white text-center">Your Learning Journey</div>
            <div className="card-body">
              {chartData ? (
                <Line data={chartData} />
              ) : (
                <p>Loading graph...</p>
              )}
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="col-md-6 mb-4">
          <div className="card h-100 shadow">
            <div className="card-header bg-dark text-white text-center">Recent Activity</div>
            <div className="card-body">
              <ul className="list-unstyled">
                {userInfo?.recentActivity?.map((activity, index) => (
                  <li key={index}>✔️ {activity}</li>
                )) || <li>No recent activity.</li>}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;
